import * as React from "react";
import { styled } from "@mui/material/styles";
import {
  Button,
  Typography,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  Box,
  PopperPlacementType,
} from "@mui/material";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { visuallyHidden } from "@mui/utils";
import { useBreakpoint } from "../hooks/useBreakpoint";

const PREFIX = "MoreInfoButton";

const classes = {
  hhiCalculation: `${PREFIX}-hhiCalculation`,
  infoButton: `${PREFIX}-infoButton`,
  pgcSideBar: `${PREFIX}-pgcSideBar`,
  popup: `${PREFIX}-popup`,
  pgcPopup: `${PREFIX}-pgcPopup`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.hhiCalculation}`]: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
    padding: "5px",
  },

  [`& .${classes.infoButton}`]: {
    "height": 40,
    "marginTop": 0,
    "marginLeft": 3,
    "overflow": "visible",
    "fontSize": "1.5rem",
    "minWidth": 40,
    "textAlign": "center",
    "borderRadius": "50%",
    "cursor": "pointer",
    "&.Mui-focusVisible": { backgroundColor: "rgba(0,0,0,.08)" },
  },

  [`& .${classes.pgcSideBar}`]: {
    "minWidth": 0,
    "height": 40,
    "width": 40,
    "marginTop": 0,
    "overflow": "visible",
    "fontSize": "1.5rem",
    "textAlign": "center",
    "borderRadius": "50%",
    "cursor": "pointer",
    "&.Mui-focusVisible": { backgroundColor: "rgba(0,0,0,.08)" },
  },

  [`& .${classes.popup}`]: {
    "border": "1px solid",
    "borderColor": theme.palette.secondary.dark,
    "maxWidth": 825,
    "boxShadow": "5px 5px 10px  #CED6E3",
    "padding": "1rem",
    "& p:not(:last-child)": {
      paddingBottom: "1rem",
    },
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "underline",
    },
  },

  [`& .${classes.pgcPopup}`]: {
    "border": "1px solid",
    "color": theme.palette.primary.main,
    "borderColor": theme.palette.secondary.dark,
    "maxWidth": 437,
    "boxShadow": "5px 5px 10px  #CED6E3",
    "padding": "1rem",
    "& p:not(:last-child)": {
      paddingBottom: "1rem",
    },
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "underline",
    },
  },
}));

interface Props {
  id: string;
  label: string;
  popperPosition: PopperPlacementType;
  children: React.ReactNode;
  hover?: boolean;
  wide?: number;
  buttonStyle?: React.CSSProperties;
  HHICalculation?: string;
  pgcSideBar?: boolean;
  pgcPopup?: boolean;
}

export default function MoreInfoButton({
  id,
  label,
  popperPosition,
  children,
  hover,
  wide,
  buttonStyle,
  HHICalculation,
  pgcSideBar,
  pgcPopup,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const handleToggle = () => {
    setOpen((prev) => !prev);
  };
  const { xs, sm, md } = useBreakpoint();

  const buttonProps =
    hover && !(xs || sm || md)
      ? {
          onMouseEnter: () => setOpen(true),
          onMouseLeave: () => setOpen(false),
          onFocus: () => setOpen(true),
          onBlur: () => setOpen(false),
          onClick: () => handleToggle(),
        }
      : { onClick: () => handleToggle() };

  const popperPros = {
    onMouseEnter: () => setOpen(true),
    onMouseLeave: () => setOpen(false),
    onFocus: () => setOpen(true),
    onBlur: () => setOpen(false),
  };
  return (
    <Root>
      <Button
        {...buttonProps}
        ref={anchorRef}
        aria-controls={open ? id : undefined}
        aria-haspopup="true"
        className={pgcSideBar ? classes.pgcSideBar : classes.infoButton}
        style={buttonStyle}
      >
        <Typography className={HHICalculation && classes.hhiCalculation}>
          {HHICalculation}
        </Typography>
        <HelpOutlineOutlinedIcon
          aria-hidden="false"
          style={{ zIndex: 10 }}
          id="tooltip"
        />
        <svg height="24" width="24" style={{ marginLeft: -24 }}>
          <circle cx="12" cy="12" r="10" fill="white" />
        </svg>
        <Typography sx={visuallyHidden}>{label}</Typography>
      </Button>
      <Popper
        tabIndex={0}
        {...popperPros}
        style={wide ? { width: wide } : undefined}
        sx={(theme) => ({
          zIndex: 100,
          ...(xs || sm || md ? { width: 200 } : {}),
        })}
        open={open}
        anchorEl={anchorRef.current}
        placement={popperPosition}
        transition
        disablePortal
      >
        {({ TransitionProps }) =>
          open ? (
            <Grow {...TransitionProps}>
              <Paper className={pgcPopup ? classes.pgcPopup : classes.popup}>
                <ClickAwayListener onClickAway={() => setOpen(false)}>
                  <Box id={id}>{children}</Box>
                </ClickAwayListener>
              </Paper>
            </Grow>
          ) : undefined
        }
      </Popper>
    </Root>
  );
}
